if (!customElements.get('myart-navigation')) {
  class MyArtNavigation extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.pendingFrames = new Set();
      this.hoverTimers = new Map();
      this.mobileDrawer = this.querySelector('[data-myart-mobile-drawer]');
      this.mobileOverlay = this.querySelector('[data-myart-mobile-overlay]');
      this.mobileTopbar = this.querySelector('[data-myart-mobile-topbar]');
      this.mobileTitle = this.querySelector('[data-myart-mobile-title]');
      this.backButton = this.querySelector('[data-myart-back]');
      this.collectionCountGrid = this.querySelector('[data-myart-collection-counts]');
      this.desktopDisclosures = [...this.querySelectorAll('[data-myart-desktop-disclosure]')];
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.phoneQuery = window.matchMedia('(width < 600px)');
      this.browserThemeColorObserver = new MutationObserver(() => this.syncBrowserThemeColor());
      this.mobileOverlay?.querySelectorAll('.myart-nav__country-dialog').forEach((dialog) => {
        this.browserThemeColorObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      });

      this.updateDesktopViewportWidth();
      window.addEventListener('resize', () => this.updateDesktopViewportWidth(), { signal: this.signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.viewportObserver = new ResizeObserver(() => this.updateDesktopViewportWidth());
        this.viewportObserver.observe(document.documentElement);
      }

      this.addEventListener('click', this.onClick.bind(this), { signal: this.signal });
      this.addEventListener('touchstart', this.onUtilityTouchStart.bind(this), { signal: this.signal, passive: true });
      this.addEventListener('touchmove', this.onUtilityTouchMove.bind(this), { signal: this.signal, passive: true });
      this.addEventListener('touchend', this.onUtilityTouchEnd.bind(this), { signal: this.signal, passive: true });
      this.addEventListener('touchcancel', this.clearUtilityActivation.bind(this), { signal: this.signal, passive: true });
      this.addEventListener('contextmenu', this.clearUtilityActivation.bind(this), { signal: this.signal });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.clearUtilityActivation();
          this.syncBrowserThemeColor();
        }
      }, { signal: this.signal });
      this.addEventListener('keydown', this.onKeydown.bind(this), { signal: this.signal });
      document.addEventListener('click', this.onDocumentClick.bind(this), { signal: this.signal });
      this.mobileQuery.addEventListener('change', this.onBreakpointChange.bind(this), { signal: this.signal });
      this.phoneQuery.addEventListener('change', () => this.syncBrowserThemeColor(), { signal: this.signal });
      document.addEventListener('shopify:block:select', this.onEditorBlockSelect.bind(this), { signal: this.signal });

      if (this.mobileDrawer) {
        this.mobileDrawer.addEventListener('toggle', this.onMobileToggle.bind(this), { signal: this.signal });
      }

      this.desktopDisclosures.forEach((disclosure) => {
        disclosure.addEventListener('toggle', () => this.onDesktopToggle(disclosure), { signal: this.signal });

        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          disclosure.addEventListener('mouseenter', () => {
            window.clearTimeout(this.hoverTimers.get(disclosure));
            this.hoverTimers.delete(disclosure);
            if (!this.mobileQuery.matches && !disclosure.open) {
              disclosure.dataset.hoverOpened = 'true';
              disclosure.open = true;
            }
          }, { signal: this.signal });
          disclosure.querySelector('summary')?.addEventListener('click', (event) => {
            if (disclosure.dataset.hoverOpened === 'true') {
              event.preventDefault();
              delete disclosure.dataset.hoverOpened;
              disclosure.open = true;
            }
          }, { signal: this.signal });
          disclosure.addEventListener('mouseleave', () => {
            window.clearTimeout(this.hoverTimers.get(disclosure));
            const timer = window.setTimeout(() => {
              this.hoverTimers.delete(disclosure);
              if (!disclosure.matches(':hover') && !disclosure.contains(document.activeElement)) {
                delete disclosure.dataset.hoverOpened;
                disclosure.open = false;
              }
            }, 100);
            this.hoverTimers.set(disclosure, timer);
          }, { signal: this.signal });
        }
      });
      if (this.mobileDrawer?.open) {
        document.body.classList.add('myart-nav-is-open');
        this.syncBrowserThemeColor();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.pendingFrames?.forEach((frame) => window.cancelAnimationFrame(frame));
      this.pendingFrames?.clear();
      this.hoverTimers?.forEach((timer) => window.clearTimeout(timer));
      this.hoverTimers?.clear();
      this.editorPanel = null;
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;
      this.browserThemeColorObserver?.disconnect();
      this.browserThemeColorObserver = null;
      this.collectionCountRequest = null;
      this.clearUtilityActivation();
      document.body.classList.remove('myart-nav-is-open');
      this.syncBrowserThemeColor(false);
    }

    afterFrame(callback) {
      const signal = this.signal;
      const frame = window.requestAnimationFrame(() => {
        this.pendingFrames.delete(frame);
        if (this.isConnected && !signal.aborted) callback();
      });
      this.pendingFrames.add(frame);
    }

    onEditorBlockSelect(event) {
      if (!window.Shopify?.designMode || !event.detail?.blockId) return;
      const matches = [...this.querySelectorAll(`[data-myart-block="${CSS.escape(event.detail.blockId)}"]`)];
      const target = matches.find((node) => this.mobileQuery.matches
        ? node.closest('[data-myart-panel]') : node.closest('.myart-nav__desktop'));
      if (!target) return;
      if (this.mobileQuery.matches && this.mobileDrawer) {
        const panel = target.closest('[data-myart-panel]');
        this.editorPanel = panel.dataset.myartPanel;
        this.mobileDrawer.open = true;
        this.showPanel(this.editorPanel);
      } else {
        const disclosure = target.closest('[data-myart-desktop-disclosure]') || target.querySelector('[data-myart-desktop-disclosure]');
        if (disclosure) disclosure.open = true;
      }
      this.afterFrame(() => target.scrollIntoView({ block: 'nearest', behavior: 'instant' }));
    }

    updateDesktopViewportWidth() {
      const width = document.documentElement.clientWidth;
      const hobby = this.querySelector('.myart-nav__desktop-disclosure > summary');
      if (hobby && !this.mobileQuery.matches) {
        this.style.setProperty('--myart-hobby-left', `${hobby.getBoundingClientRect().left}px`);
      }
      if (width > 0 && width !== this.desktopViewportWidth) {
        this.style.setProperty('--myart-nav-viewport-width', `${width}px`);
        this.desktopViewportWidth = width;
      }
    }

    clearUtilityActivation() {
      this.utilityTouch = null;
      this.querySelectorAll('.myart-nav__mobile-utilities a.is-activated')
        .forEach((link) => link.classList.remove('is-activated'));
    }

    onUtilityTouchStart(event) {
      if (!this.mobileQuery.matches || event.touches.length !== 1) {
        this.clearUtilityActivation();
        return;
      }
      const link = event.target.closest('.myart-nav__mobile-utilities a[href]');
      if (!link || !this.contains(link)) {
        this.clearUtilityActivation();
        return;
      }
      const touch = event.changedTouches[0];
      this.clearUtilityActivation();
      this.utilityTouch = { link, identifier: touch.identifier, x: touch.clientX, y: touch.clientY };
      link.classList.add('is-activated');
    }

    onUtilityTouchMove(event) {
      const start = this.utilityTouch;
      if (!start) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === start.identifier);
      if (touch && Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 10) {
        this.clearUtilityActivation();
      }
    }

    onUtilityTouchEnd(event) {
      const start = this.utilityTouch;
      if (!start) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === start.identifier);
      if (!touch) return;
      this.utilityTouch = null;
      if (!start.link.contains(document.elementFromPoint(touch.clientX, touch.clientY))) {
        this.clearUtilityActivation();
      }
    }

    onClick(event) {
      const utilityLink = event.target.closest('.myart-nav__mobile-utilities a[href]');
      if (utilityLink && this.mobileQuery.matches && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        this.clearUtilityActivation();
        utilityLink.classList.add('is-activated');
        queueMicrotask(() => {
          if (event.defaultPrevented) this.clearUtilityActivation();
        });
      }

      const openTrigger = event.target.closest('[data-myart-open]');
      if (openTrigger && this.contains(openTrigger)) {
        const panel = this.querySelector(`[data-myart-panel="${CSS.escape(openTrigger.dataset.myartOpen)}"]`);
        if (panel) {
          event.preventDefault();
          this.showPanel(panel.dataset.myartPanel);
        }
        return;
      }

      if (event.target.closest('[data-myart-back]')) {
        const activePanel = this.querySelector('[data-myart-panel]:not([hidden])');
        this.showPanel(activePanel?.dataset.parent || 'root', true);
        return;
      }

      if (event.target.closest('[data-myart-close]')) this.closeMobileDrawer(true);
    }

    onKeydown(event) {
      // The native dialog owns Escape and focus while the country picker is open.
      if (this.querySelector('.myart-nav__country-dialog[open]')) return;
      if (event.key === 'Escape') {
        const openDesktop = this.desktopDisclosures.find((disclosure) => disclosure.open);
        if (openDesktop) {
          openDesktop.open = false;
          openDesktop.querySelector('summary')?.focus();
          return;
        }

        if (this.mobileDrawer?.open) {
          event.preventDefault();
          this.closeMobileDrawer(true);
        }
        return;
      }

      if (event.key === 'Tab' && this.mobileDrawer?.open && this.mobileQuery.matches) this.trapMobileFocus(event);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.closeDesktopDisclosures();
    }

    onDesktopToggle(currentDisclosure) {
      if (!currentDisclosure.open) return;
      this.desktopDisclosures.forEach((disclosure) => {
        if (disclosure !== currentDisclosure) disclosure.open = false;
      });
    }

    onMobileToggle() {
      const isOpen = Boolean(this.mobileDrawer?.open);
      if (!isOpen) {
        this.clearUtilityActivation();
        this.syncBrowserThemeColor(false);
      }
      document.body.classList.toggle('myart-nav-is-open', isOpen);
      if (isOpen) {
        this.showPanel(this.editorPanel || 'root');
        this.editorPanel = null;
        this.afterFrame(() => {
          if (this.mobileDrawer?.open) this.querySelector('[data-myart-close]')?.focus({ preventScroll: true });
        });
      }
    }

    onBreakpointChange() {
      this.closeDesktopDisclosures();
      if (!this.mobileQuery.matches) this.closeMobileDrawer(false);
    }

    showPanel(panelId, restoreFocus = false) {
      this.clearUtilityActivation();
      const nextPanel = this.querySelector(`[data-myart-panel="${CSS.escape(panelId)}"]`);
      if (!nextPanel) return;
      const previousPanel = this.querySelector('[data-myart-panel]:not([hidden])');

      this.querySelectorAll('[data-myart-panel]').forEach((panel) => {
        panel.hidden = panel !== nextPanel;
      });

      if (this.mobileTitle) this.mobileTitle.textContent = nextPanel.dataset.title || 'Menu';
      if (this.mobileTopbar) this.mobileTopbar.dataset.panel = panelId;
      if (this.backButton) this.backButton.hidden = panelId === 'root';
      this.querySelector('.myart-nav__mobile-scroll')?.scrollTo({ top: 0, behavior: 'instant' });
      if (panelId === 'paint-by-numbers' || panelId === 'collections') this.ensureCollectionCounts();
      this.syncBrowserThemeColor();

      if (restoreFocus && previousPanel) {
        const trigger = nextPanel.querySelector(`[data-myart-open="${CSS.escape(previousPanel.dataset.myartPanel)}"]`);
        this.afterFrame(() => {
          if (this.isConnected && !nextPanel.hidden && trigger?.getClientRects().length) trigger.focus({ preventScroll: true });
        });
      } else if (panelId !== 'root') {
        this.afterFrame(() => {
          if (this.mobileDrawer?.open && !nextPanel.hidden) this.backButton?.focus({ preventScroll: true });
        });
      }
    }

    // CSS owns the bottom surface: root footer or nested-panel canvas.
    // Preserve host metadata on close, tablet/desktop resize and editor teardown.
    syncBrowserThemeColor(active = Boolean(this.phoneQuery?.matches && this.mobileDrawer?.open)) {
      if (active) {
        const edgeColor = getComputedStyle(this).getPropertyValue('--myart-nav-document-edge').trim();
        if (!edgeColor) return;
        if (!this.browserThemeColors) {
          this.browserThemeColors = [...document.querySelectorAll('meta[name="theme-color"]')]
            .map((meta) => ({ meta, content: meta.getAttribute('content') }));
        }
        this.browserThemeColors.forEach(({ meta }) => {
          if (!meta.isConnected || (this.browserThemeColorApplied && meta.getAttribute('content') !== this.browserThemeColorApplied)) return;
          meta.setAttribute('content', edgeColor);
        });
        this.browserThemeColorApplied = edgeColor;
      } else {
        this.browserThemeColors?.forEach(({ meta, content }) => {
          if (!meta.isConnected || meta.getAttribute('content') !== this.browserThemeColorApplied) return;
          if (content === null) meta.removeAttribute('content');
          else meta.setAttribute('content', content);
        });
        this.browserThemeColors = null;
        this.browserThemeColorApplied = null;
      }
    }

    ensureCollectionCounts() {
      const grid = this.collectionCountGrid;
      if (!grid || grid.dataset.countsReady === 'true') return Promise.resolve(true);
      if (this.collectionCountRequest) return this.collectionCountRequest;

      grid.dataset.countsState = 'loading';
      grid.setAttribute('aria-busy', 'true');
      const signal = this.signal;
      const request = this.fetchCollectionCounts(grid)
        .then(() => true)
        .catch((error) => {
          if (error.name !== 'AbortError' && this.isConnected && !signal.aborted) grid.dataset.countsState = 'unavailable';
          return false;
        })
        .finally(() => {
          if (this.collectionCountRequest === request) this.collectionCountRequest = null;
          if (this.isConnected && !signal.aborted) grid.setAttribute('aria-busy', 'false');
        });
      this.collectionCountRequest = request;
      return request;
    }

    async fetchCollectionCounts(grid) {
      const sourceUrl = grid.dataset.sourceUrl;
      const signal = this.signal;
      const sectionId = this.dataset.sectionId;
      if (!sourceUrl || !sectionId) throw new Error('Missing collection count source');

      const requestUrl = new URL(sourceUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);
      const response = await fetch(requestUrl, {
        signal,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Collection count request failed: ${response.status}`);

      const sourceDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const sourceGrid = sourceDocument.querySelector('[data-myart-collection-counts][data-counts-ready="true"]');
      if (!sourceGrid) throw new Error('Collection count source is unavailable');

      const countByValue = new Map();
      sourceGrid.querySelectorAll('[data-myart-filter-value]').forEach((card) => {
        const count = card.querySelector('[data-myart-filter-count]:not([hidden])');
        const value = card.dataset.myartFilterValue?.trim().toLowerCase();
        if (value && count) countByValue.set(value, count.textContent.trim());
      });
      if (!countByValue.size) throw new Error('Collection count source is empty');
      if (!this.isConnected || signal.aborted) throw new DOMException('Aborted', 'AbortError');

      let updated = 0;
      grid.querySelectorAll('[data-myart-filter-value]').forEach((card) => {
        const value = card.dataset.myartFilterValue?.trim().toLowerCase();
        const count = card.querySelector('[data-myart-filter-count]');
        if (!value || !count || !countByValue.has(value)) return;
        count.textContent = countByValue.get(value);
        count.hidden = false;
        updated += 1;
      });
      if (!updated) throw new Error('Collection counts did not match menu values');

      grid.dataset.countsReady = 'true';
      grid.dataset.countsState = 'ready';
    }

    closeDesktopDisclosures() {
      this.desktopDisclosures.forEach((disclosure) => {
        disclosure.open = false;
      });
    }

    closeMobileDrawer(restoreFocus) {
      this.clearUtilityActivation();
      if (!this.mobileDrawer?.open) return;
      const summary = this.mobileDrawer.querySelector('summary');
      this.mobileDrawer.open = false;
      document.body.classList.remove('myart-nav-is-open');
      this.syncBrowserThemeColor(false);
      if (restoreFocus) this.afterFrame(() => {
        if (!this.mobileDrawer?.open) summary?.focus({ preventScroll: true });
      });
    }

    trapMobileFocus(event) {
      const focusable = [...this.mobileOverlay.querySelectorAll('a[href], button:not([disabled]), summary, input, select, textarea, [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('myart-navigation', MyArtNavigation);
}
